/**
 * Human-readable copy for the `?error=<code>` values that
 * `GET /api/auth/sso/callback` redirects back to `/login` with.
 *
 * The codes are a closed set owned by the backend. Anything unrecognised
 * falls back to a generic message — the raw code is never rendered, so a
 * crafted `?error=` value cannot put attacker-controlled text on the page.
 */
const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_state_mismatch:
    "That sign-in link has expired or was already used. Please try signing in again.",
  sso_domain_refused: "This Microsoft account is not permitted to access Spare Parts ID.",
  sso_token_invalid: "Microsoft could not verify your sign-in. Please try again.",
  sso_failed: "Microsoft sign-in did not complete. Please try again.",
  // The address already belongs to a different Microsoft account — most likely a
  // former employee's address reissued to a new hire. The backend refuses rather
  // than re-linking, because re-linking would hand over that person's chat
  // history and role. Deliberately routed to a human: there is no self-service
  // resolution, and telling the user to try again would be advice that can only
  // fail.
  sso_account_conflict:
    "This email address is already linked to a different Microsoft account. Please contact your administrator.",
  // Raised when the ENTRA_* settings are empty, i.e. SSO is not wired up in this
  // environment at all. Distinct copy on purpose: the generic fallback tells the
  // user to try again, and retrying will never work. This is the state a local
  // dev environment is in by default — and since the password form is gone,
  // it is now a dead end rather than an inconvenience, so the copy says so.
  sso_not_configured:
    "Microsoft sign-in is not set up in this environment, so there is no way to sign in here.",
};

const GENERIC_SSO_ERROR = "Microsoft sign-in failed. Please try again.";

/**
 * Maps an `?error=` query value to display copy.
 * Returns an empty string when there is no error to show.
 */
export function ssoErrorMessage(code: string | null | undefined): string {
  if (!code) {
    return "";
  }
  // Own-property check only: a plain object inherits `constructor`, `toString`
  // and friends, so a bare lookup on `?error=constructor` would hand back a
  // function instead of falling through to the generic message.
  const message = Object.hasOwn(SSO_ERROR_MESSAGES, code) ? SSO_ERROR_MESSAGES[code] : undefined;
  return message ?? GENERIC_SSO_ERROR;
}
